import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import {
    BuildOutlined as RepairIcon,
    ChevronRight as ChevronRightIcon,
    DirectionsCar as ServiceIcon,
    StarBorderOutlined as StarBorderOutlinedIcon,
    StarRate as StarRateIcon,
} from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Card from '@/components/ui/Card/Card';
import Divider from '@/components/ui/Divider/Divider';
import Paper from '@/components/ui/Paper/Paper';
import Typography from '@/components/ui/Typography/Typography';
import EditDetailsModal from '@/components/business/EditDetailsModal/EditDetailsModal';
import ReviewModal from '@/components/business/ReviewModal/ReviewModal';
import type { EditDetailsFormValues } from '@/components/business/EditDetailsModal/IEditDetailsModal';
import type { ReviewSubmitResult } from '@/components/business/ReviewModal/IReviewModal';
import { useChat } from '@/providers/ChatProvider/ChatProvider';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import { useJobs } from '@/providers/JobsProvider/JobsProvider';
import { jobsApi } from '@/api/jobs';
import type { Job, Service } from '@/types/types';
import type { JobDetailsProps } from './IJobDetails';
import { styles } from './styles';

const capitalize = (s: string | null | undefined) =>
    !s ? '' : s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

const buildYYMC = (job: Job): string => {
    const v = job.vehicle;
    if (!v) return '';
    const parts: string[] = [];
    if (v.year || v.make || v.model) {
        parts.push(
            `${v.year ?? ''} ${capitalize(v.make)} ${capitalize(v.model)}`
                .replace(/\s+/g, ' ')
                .trim(),
        );
    }
    if (v.color) parts.push(capitalize(v.color));
    return parts.join(', ');
};

const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

interface ServicesByRepairer {
    repairer: { id: string; name: string };
    services: Service[];
    total: number;
}

const groupByRepairer = (services: Service[]): ServicesByRepairer[] => {
    const map = new Map<string, ServicesByRepairer>();
    services.forEach((s) => {
        const id = s.repair_company?.id ?? 'unknown';
        const name = s.repair_company?.name ?? 'Unknown';
        if (!map.has(id)) {
            map.set(id, { repairer: { id, name }, services: [], total: 0 });
        }
        const group = map.get(id)!;
        group.services.push(s);
        group.total += s.price ?? 0;
    });
    return Array.from(map.values());
};

const findChannelUrl = (job: Job, repairerId: string): string | null => {
    const channels = job.chat?.channels;
    if (!channels) return null;
    if (Array.isArray(channels)) {
        const found = channels.find((c) => String(c.company_id) === String(repairerId));
        return found?.channel_url ?? channels[0]?.channel_url ?? null;
    }
    if (typeof channels === 'object') {
        const byId = channels[repairerId];
        if (byId?.channel_url) return byId.channel_url;
        const first = Object.values(channels)[0];
        return first?.channel_url ?? null;
    }
    return null;
};

const JobDetails = ({ job, isRepairer }: JobDetailsProps) => {
    const theme = useTheme();
    const chat = useChat();
    const jobs = useJobs();
    const err = useError();
    const [editOpen, setEditOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewCompany, setReviewCompany] = useState<{ id: string; name: string } | null>(null);

    const dateCreated = job.created_at
        ? `Created on: ${new Date(job.created_at).toDateString()}`
        : '';
    const stockRO = `Stock/RO: ${job.vehicle?.stock_number ?? ''}`;
    const vin = `VIN: ${job.vehicle?.vin ?? ''}`;
    const po = `PO#: ${job.po_number ?? ''}`;
    const location = job.location?.nickname ?? job.location?.formatted_address ?? '';
    const serviceCount = job.services.length;
    const repairerCount = new Set(
        job.services.map((s) => s.repair_company?.name).filter(Boolean),
    ).size;
    const total = job.services.reduce((sum, s) => sum + (s.price ?? 0), 0);

    const groups = useMemo(() => groupByRepairer(job.services), [job.services]);

    const CANCELLABLE = new Set(['OPEN']);
    const DECLINABLE = new Set(['OPEN', 'ACCEPTED', 'IN_PROGRESS']);
    const statusNorm = (status: string) =>
        status.toUpperCase().replace(/ /g, '_');
    const hasCancellable = job.services.some((s) =>
        CANCELLABLE.has(statusNorm(s.service_status)),
    );
    const hasDeclinable = job.services.some((s) =>
        DECLINABLE.has(statusNorm(s.service_status)),
    );

    const allStarred = useMemo(() => {
        if (!job.reviews) return false;
        const ids = Array.from(
            new Set(job.services.map((s) => s.repair_company?.id).filter(Boolean)),
        );
        return ids.every((id) =>
            job.reviews?.some((r) => r.reviewed_company.id === id),
        );
    }, [job]);

    const ratingFor = (companyId: string) =>
        job.reviews?.find((r) => r.reviewed_company.id === companyId)?.rating ?? null;

    const handleEditSave = async (values: EditDetailsFormValues) => {
        try {
            await jobsApi.update(job.id, {
                vehicle: {
                    ...job.vehicle,
                    stock_number: values.stockNumber,
                    color: values.color,
                    odometer: values.odometer
                        ? { unit: values.odometerUnit, value: Number(values.odometer) }
                        : null,
                },
                po_number: values.poNumber,
            } as Partial<Job>);
            await jobs.actions.refreshJob(job.id);
        } catch (e) {
            err.DISPATCH({ type: 'API_ERROR', payload: e as never });
        }
    };

    const handleReviewSubmitted = (_result: ReviewSubmitResult) => {
        void jobs.actions.refreshJob(job.id);
    };

    const handleOpenChat = (group: ServicesByRepairer) => {
        const url = findChannelUrl(job, group.repairer.id);
        if (!url) return;
        chat.openWithChannel({
            url,
            name: group.repairer.name,
        });
    };

    return (
        <Paper sx={styles.paper}>
            <Box sx={styles.headerRow}>
                <Typography sx={styles.dateText}>{dateCreated}</Typography>
                <Box sx={styles.actionsRight}>
                    <Button sx={styles.actionBtn(theme)} onClick={() => setEditOpen(true)}>
                        Edit Details
                    </Button>
                    {isRepairer && hasDeclinable && (
                        <Button sx={styles.declineBtn(theme)}>Decline Job</Button>
                    )}
                    {!isRepairer && hasCancellable && (
                        <Button sx={styles.cancelBtn(theme)}>Cancel Job</Button>
                    )}
                </Box>
            </Box>
            <Divider sx={styles.divider} />
            <Typography sx={styles.title}>{buildYYMC(job)}</Typography>
            <Box sx={styles.metaRow}>
                <Box>
                    <Typography sx={styles.meta}>{stockRO}</Typography>
                    <Typography sx={styles.metaVin}>{vin}</Typography>
                    <Typography sx={styles.meta}>{location}</Typography>
                </Box>
                <Typography sx={styles.meta}>{po}</Typography>
            </Box>
            <Divider sx={styles.divider} />
            <Box sx={styles.countsRow}>
                <Box sx={styles.countsLeft}>
                    <ServiceIcon sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                    <Typography sx={styles.countValue}>{serviceCount}</Typography>
                    <RepairIcon sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                    <Typography sx={styles.countValue}>{repairerCount}</Typography>
                    {allStarred ? (
                        <StarRateIcon sx={{ ml: 0.5, color: theme.palette.grey[400], fontSize: 18 }} />
                    ) : (
                        <StarBorderOutlinedIcon
                            sx={{ ml: 0.5, color: theme.palette.grey[400], fontSize: 18 }}
                        />
                    )}
                </Box>
                <Typography sx={styles.total}>{formatCurrency(total)}</Typography>
            </Box>
            <Typography sx={styles.repairersTitle}>
                {isRepairer ? 'Your Services' : 'Services by Repairer'}
            </Typography>
            {groups.map((group) => {
                const rating = ratingFor(group.repairer.id);
                return (
                    <Card key={group.repairer.id} sx={styles.repairerCard(theme)}>
                        <Box sx={styles.repairerHeader(theme)}>
                            <Typography sx={styles.repairerName}>{group.repairer.name}</Typography>
                            <Box sx={styles.repairerHeaderRight}>
                                {!isRepairer && rating == null && (
                                    <Button
                                        startIcon={<StarBorderOutlinedIcon />}
                                        sx={styles.rateLink(theme)}
                                        onClick={() => {
                                            setReviewCompany(group.repairer);
                                            setReviewOpen(true);
                                        }}
                                    >
                                        Rate this vendor
                                    </Button>
                                )}
                                {!isRepairer && rating != null && (
                                    <Box sx={styles.rated(theme)}>
                                        <Typography>{rating} /</Typography>
                                        <StarRateIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                )}
                                <Typography sx={styles.repairerTotal}>
                                    {formatCurrency(group.total)}
                                </Typography>
                            </Box>
                        </Box>
                        {group.services.map((s) => (
                            <Box key={s.id} sx={styles.serviceItem(theme)}>
                                <Box sx={styles.serviceHeader}>
                                    <Typography sx={styles.serviceTitle}>
                                        {s.service_details?.service_type ?? 'Service'}
                                        {(s.service_details?.repair_type ||
                                            s.service_details?.part) && (
                                            <Typography
                                                component="span"
                                                sx={{ ...styles.serviceMeta, ml: 1 }}
                                            >
                                                {[
                                                    s.service_details?.repair_type,
                                                    s.service_details?.part,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography sx={styles.serviceTitle}>
                                        {formatCurrency(s.price ?? 0)}
                                    </Typography>
                                </Box>
                                <Card
                                    // @ts-expect-error custom card variants from theme augmentation
                                    variant={statusNorm(s.service_status)}
                                    sx={{ mt: 1 }}
                                >
                                    {s.service_status}
                                </Card>
                                <Box sx={{ ...styles.serviceLinkRow(theme), pt: 1.5 }}>
                                    <Typography sx={styles.serviceLink(theme)}>
                                        History
                                    </Typography>
                                    <ChevronRightIcon sx={{ color: theme.palette.blue[700] }} />
                                </Box>
                                <Box
                                    sx={styles.serviceLinkRow(theme)}
                                    onClick={() => handleOpenChat(group)}
                                >
                                    <Typography sx={styles.serviceLink(theme)}>
                                        Chat with repairer
                                    </Typography>
                                    <ChevronRightIcon sx={{ color: theme.palette.blue[700] }} />
                                </Box>
                            </Box>
                        ))}
                    </Card>
                );
            })}
            <EditDetailsModal
                open={editOpen}
                job={job}
                onClose={() => setEditOpen(false)}
                onSave={handleEditSave}
            />
            <ReviewModal
                open={reviewOpen}
                jobID={job.id}
                repairerCompany={reviewCompany}
                onClose={() => setReviewOpen(false)}
                onSubmit={handleReviewSubmitted}
            />
        </Paper>
    );
};

export default JobDetails;
